import { useMutation } from "@tanstack/react-query";

import { mutatePublicData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
  getHttpStatus,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";

/** Corps de `POST /gw/user/api/v1/Users/add` (guide §6.9 — 5 champs, aucun rôle). */
export type AddKeycloakUserPayload = {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
};

/** Réponse 201 : `keycloakUserId` (claim sub) alimente la Party et le wallet. */
export type AddKeycloakUserResponse = {
  keycloakUserId: string;
  username?: string;
  email?: string;
  emailVerificationSent?: boolean;
  emailVerificationRequired?: boolean;
};

/** 409 — username ou email déjà pris (compte Keycloak existant). */
export const isKeycloakUserConflict = (error: unknown): boolean =>
  getHttpStatus(error) === 409;

/** 400 — mot de passe refusé par la politique (message backend à afficher). */
export const isKeycloakPasswordRejected = (error: unknown): boolean =>
  getHttpStatus(error) === 400;

/**
 * Crée le compte Keycloak du client (`POST /gw/user/api/v1/Users/add`) —
 * étape 1 du parcours d'inscription. Endpoint PUBLIC → `mutatePublicData`.
 * Register public : hors scope de la preview de confirmation (règle 1).
 *
 * Chemin canonique : `authEndpoints.addUser` ré-exporté par
 * `@/core/api/endpoints` (kyc n'importe pas `features/auth`).
 */
export const useAddKeycloakUser = () => {
  return useMutation({
    mutationKey: QUERY_KEYS.AUTH_ADD_USER(),
    mutationFn: (body: AddKeycloakUserPayload) =>
      mutatePublicData({
        url: endpoints.auth.addUser,
        reqBody: body,
        method: "POST",
      }) as Promise<AddKeycloakUserResponse>,
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useAddKeycloakUser failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
